import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Youtube, Facebook, ArrowUpRight } from "lucide-react";

const linkGroups = [
    {
        title: "Platform",
        links: [
            { label: "Home", to: "/" },
            { label: "Services", to: "/#services" },
            { label: "Events", to: "/events" },
            { label: "Gallery", to: "/gallery" },
        ],
    },
    {
        title: "Engage",
        links: [
            { label: "Request a Meeting", to: "/request-meeting" },
            { label: "Submit a Grievance", to: "/grievance" },
            { label: "Share Ideas", to: "/share-ideas" },
            { label: "Volunteer", to: "/volunteer" },
        ],
    },
    {
        title: "About",
        links: [
            { label: "About", to: "/about" },
            { label: "Contact", to: "/contact" },
            { label: "Privacy Policy", to: "/#" },
            { label: "Terms", to: "/#" },
        ],
    },
];

export default function Footer() {
    return (
        <footer className="relative mt-24 border-t border-ink/10 bg-white" data-testid="site-footer">
            {/* Editorial word */}
            <div className="container-x pb-12 pt-24">
                <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <div className="eyebrow mb-4">Concept · Design Prototype</div>
                        <h3 className="h-display font-display text-[44px] font-black leading-[0.95] text-ink md:text-[56px]">
                            Built for citizens.
                            <br />
                            Not for optics.
                        </h3>
                        <p className="mt-6 max-w-md text-[15px] leading-relaxed text-slate-500">
                            A design study exploring what a modern, transparent citizen engagement platform could look
                            and feel like. No official association is implied.
                        </p>
                        <Link
                            to="/request-meeting"
                            className="btn-primary mt-8 group"
                            data-testid="footer-cta-request"
                        >
                            Request a Meeting
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-2 gap-8 md:grid-cols-3 lg:col-span-7">
                        {linkGroups.map((g) => (
                            <div key={g.title}>
                                <div className="eyebrow mb-4">{g.title}</div>
                                <ul className="space-y-3">
                                    {g.links.map((l) => (
                                        <li key={l.label}>
                                            <Link
                                                to={l.to}
                                                className="text-[14px] text-ink/80 transition-colors hover:text-pink"
                                            >
                                                {l.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Giant wordmark */}
                <div className="mt-20 border-t border-ink/10 pt-10">
                    <div
                        className="serif-italic pointer-events-none select-none text-center text-[18vw] font-black leading-[0.85] text-ink/95 md:text-[14vw]"
                        style={{ fontFamily: "Playfair Display", letterSpacing: "-0.03em" }}
                    >
                        MeetWith<span className="italic text-pink">KTR.</span>
                    </div>
                </div>

                <div className="mt-10 flex flex-col items-start justify-between gap-6 border-t border-ink/10 pt-8 md:flex-row md:items-center">
                    <div className="flex items-center gap-3 text-[12px] text-slate-500">
                        <img
                            src="/assets/party-logo.png"
                            alt="Party emblem"
                            className="h-8 w-8 rounded-full object-cover ring-1 ring-ink/10"
                        />
                        © {new Date().getFullYear()} MeetWithKTR (Concept). All rights reserved.
                    </div>
                    <div className="flex items-center gap-3">
                        {[Instagram, Twitter, Youtube, Facebook].map((Icon, i) => (
                            <a
                                key={i}
                                href="#"
                                onClick={(e) => e.preventDefault()}
                                className="grid h-10 w-10 place-items-center rounded-full border border-ink/10 text-ink/70 transition-colors hover:border-pink hover:text-pink"
                                data-testid={`footer-social-${i}`}
                                aria-label="social"
                            >
                                <Icon className="h-4 w-4" />
                            </a>
                        ))}
                    </div>
                </div>

                <div className="mt-8 rounded-2xl border border-ink/10 bg-slate-50 p-4 text-[12px] leading-relaxed text-slate-500" data-testid="footer-disclaimer">
                    <strong className="font-semibold text-ink">Disclaimer:</strong> This website is an independent
                    concept/demo and is not an official platform unless explicitly authorized. Names, imagery and data
                    are illustrative only.
                </div>
            </div>
        </footer>
    );
}
